<template>
<div class="user-component">
  <header class="user-component__header">
    <h3 class="user-component__header-title">Профиль</h3>
    <div class="invisible flex-grow"/>
    <button type="button" class="user-component__header-button" @click="editMode = !editMode">
      {{ !editMode ? 'Редактировать' : 'Отмена' }}
    </button>
  </header>
  <form @submit.prevent="saveChanges">
    <section class="user-component__base-info-section">
      <responsive-image v-if="avatarMedia" :media="avatarMedia" :sizes="['medium', 'thumb']" alt="Avatar" />
      <img v-else :src="avatarUrl" class="user-component__avatar" alt="Avatar">
      <h4 class="user-component__name">
        <span v-if="!editMode">{{ user.customer.full_name || '' }}</span>
        <div v-else>
          <CustomInput label="Имя" class="user-component__first-name-input" v-model="form.firstName" />
          <CustomInput label="Фамилия" class="user-component__last-name-input" v-model="form.lastName" />
        </div>
      </h4>
      <template v-if="loyaltyEnabled">
        <div class="user-component__base-info-numbers">
          <h5>Бонусы</h5>
          <p>{{ loyaltyProfile && priceToText(loyaltyProfile.balance) || '...' }}</p>
          <h5>Кэшбэк</h5>
          <p>{{ loyaltyProfile && loyaltyProfile.rates.cashback_rate.percentage + '%' || '...' }}</p>
          <h5>Потрачено</h5>
          <p>{{ loyaltyProfile && priceToText(loyaltyProfile.expenses) || '...' }}</p>
        </div>
        <p v-if="balanceAdjustmentEnabled" class="mt-2">
          <a v-if="canAdjustBalance" href="javascript:;" class="text-primary text-xs" @click="adjustBalance">
            <i class="fa fa-sync"></i>
            Обновить баланс
          </a>
          <span v-else class="text-gray-900 text-xs">
            Баланс будет обновлён в течение 5 минут.
          </span>
        </p>
      </template>
    </section>
    <div v-if="loyaltyEnabled" v-swiper="swiperProps" class="swiper-container">
      <ol class="user-component__bonuses-levels swiper-wrapper">
        <template v-if="bonusesLevels && loyaltyProfile">
          <li v-for="(level, index) of bonusesLevels"
              :key="level.id"
              class="user-component__bonuses-level-card swiper-slide"
          >
            <header class="user-component__bonuses-level-card-header">
              <p class="user-component__bonuses-level-marker">{{ index + 1 }}</p>
              <p class="user-component__bonuses-level-name">{{ level.name }}</p>
              <div class="flex-grow"></div>
              <p class="user-component__bonuses-level-cashback-rate">{{ level.cashback_rate.percentage }}%</p>
            </header>
            <div class="flex-grow"/>
            <hr v-if="!getIsDividerAtAlternativePosition(index)" class="user-component__bonuses-level-divider">
            <p class="user-component__bonuses-level-progress-text">{{ levelCardProgresses[index].text }}</p>
            <hr v-if="getIsDividerAtAlternativePosition(index)" class="user-component__bonuses-level-divider">
            <div class="user-component__bonuses-level-progress"
                 role="progressbar"
                 :aria-valuenow="levelCardProgresses[index].value.trimmed"
                 :aria-valuemin="levelCardProgresses[index].min.trimmed"
                 :aria-valuemax="levelCardProgresses[index].max.trimmed"
            >
              <div v-if="levelCardProgresses[index].scaleRelativeToPrevious"
                   class="user-component__bonuses-level-progress-labels"
              >
                <p>{{ priceToText(levelCardProgresses[index].min) }}</p>
                <p>{{ priceToText(levelCardProgresses[index].max) }}</p>
              </div>
              <div class="user-component__bonuses-level-progress-bar">
                <div class="user-component__bonuses-level-progress-bar-fill"
                     :style="{ width: `${getLevelCardProgressPercentage(index)}%` }"
                />
              </div>
            </div>
            <button v-if="shouldDisplayBonusesPin && (userLevelInd === index || userLevelInd === -1 && index === 0)"
                    @click="showPinCode = !showPinCode"
                    type="button"
                    class="user-component__show-pin-button btn"
            >
              <template v-if="!showPinCode">Показать код</template>
              <template v-else>
                <template v-if="pinCode">{{ pinCode }}</template>
                <i v-else class="fa fa-spinner fa-spin"/>
              </template>
            </button>
          </li>
        </template>
      </ol>
    </div>
    <section class="user-component__detail-info-section">
      <CustomField label="Дата регистрации" class="user-component__detail-info-section-item">
        <span class="user-component__info-data">{{ formatDatetime(user.customer.created_at) }}</span>
      </CustomField>
      <CustomField label="Номер телефона" class="user-component__detail-info-section-item">
        <span class="user-component__info-data">{{ user.customer.phone.international }}</span>
      </CustomField>
      <!-- TODO uncomment when emails aren't just randomly generated. -->
      <!-- <p class="user-component__detail-info-section-item">
        <span class="user-component__info-label">E-mail</span>
        <span class="user-component__info-data">{{ user.email }}</span>
      </p> -->
      <CustomField label="Пол" class="user-component__detail-info-section-item">
        <span v-if="!editMode" class="user-component__info-data">{{ user.customer.gender.name }}</span>
        <CustomSelect v-else
                      :options="genderOptions"
                      v-model="form.gender"
                      :reduce="o => o.id"
                      :clearable="false"
                      :searchable="false"
        />
      </CustomField>
      <CustomField label="День рождения" class="user-component__detail-info-section-item">
        <!-- TODO remove `instanceof Array` when it stops returning an array. -->
        <span v-if="!editMode" class="user-component__info-data">
          {{ user.customer.birth_date && !(user.customer.birth_date instanceof Array) && formatDatetime(user.customer.birth_date) || 'Не указан' }}
        </span>
        <DateInput v-else v-model="form.birthDate" autocomplete="bday" />
      </CustomField>
      <CustomField label="Реферральная ссылка"
                   class="user-component__detail-info-section-item"
                   v-if="!editMode && loyaltyEnabled && hasReferralUrls"
      >
        <div class="user-component__info-data break-all">
          <p>
            <a :href="user.customer.loyalty.referral_program.join_url" target="_blank" class="font-bold underline">
              {{ user.customer.loyalty.referral_program.join_url }}
            </a>
          </p>
          <p class="mt-4">
            <a :href="user.customer.loyalty.referral_program.invite_url" target="_blank" class="underline text-dark-lighter">
              Подробности
            </a>
          </p>
        </div>
      </CustomField>
    </section>
    <div v-if="editMode" class="px-5 my-4">
      <button type="submit" class="relative inline-flex items-center justify-center btn btn-primary btn-rounded">
        <span :style="{ visibility: savingChanges ? 'hidden' : undefined }">Сохранить</span>
        <i v-if="savingChanges" class="absolute fa fa-spinner fa-spin" />
      </button>
      <button @click="editMode = false" type="button" class="btn btn-primary-outline btn-rounded ml-2">
        Отмена
      </button>
    </div>
  </form>
</div>
</template>

<script>
import axios from '@/zenky'
import priceToText from '@/helpers/priceToText'
import createZeroPrice from '@/helpers/createZeroPrice';
import unreliablyFormatRublesPrice from '@/helpers/unreliablyFormatRublesPrice'
import { mapActions, mapGetters } from 'vuex'
import ResponsiveImage from '@/components/images/ResponsiveImage'
import { getErrorMessage } from "@/helpers/apiErrors";
import storage from "@/storage";

function createFormData(user) {
  return {
    firstName: user.customer.first_name || '',
    lastName: user.customer.last_name || '',
    gender: user.customer.gender.id,
    birthDate: user.customer.birth_date?.datetime?.slice(0, 10) || null,
  };
}

const nextBalanceAdjustment = parseInt(storage.nextBalanceAdjustment(), 10);
const currentTime = new Date().getTime();

export default {
  components: {
    ResponsiveImage,
    CustomField: () => import('@/components/inputs/CustomField'),
    CustomInput: () => import('@/components/inputs/CustomInput'),
    CustomSelect: () => import('@/components/inputs/CustomSelect'),
    DateInput: () => import('@/components/inputs/DateInput'),
  },

  directives: {
    swiper: {
      async bind(el, binding, vnode) {
        el.classList.add('swiper-not-ready');

        const styleP = import(/* webpackPreload: true */ 'swiper/css/swiper.min.css')
        const { Swiper } = (await import(/* webpackPreload: true */ './treeshakenSwiper'));
        await styleP;

        if (el.swiperUnbound) return;

        el.classList.remove('swiper-not-ready');
        el.swiper = new Swiper(el, binding.value);

        el.swiperUnwatch = vnode.context.$watch(
          () => [vnode.context.bonusesLevels, vnode.context.loyaltyProfile],
          () => el.swiper.update(),
        );
      },
      unbind(el) {
        if (el.swiper) {
          el.swiperUnwatch();
          el.swiper.destroy();
        } else {
          el.swiperUnbound = true;
        }
      },
    },
  },

  props: {
    user: {
      type: Object,
      required: true,
    },

    loyaltyProfile: {
      validator: v => v instanceof Object || v === null,
      required: true,
    },

    bonusesLevels: {
      validator: v => v instanceof Array || v === null,
      required: true,
    },
  },

  created() {
    const unwathShowPinCode = this.$watch(
      () => this.showPinCode,
      async n => {
        if (n) {
          this.pinCode = (await axios.get('/loyalty/profile/pin')).data.data.pin;

          await this.$nextTick(); // Because unwatchShowPin is undefined at this point.
          unwathShowPinCode();
        }
      },
      { immediate: true },
    )
  },

  computed: {
    ...mapGetters({
      avatarUrl: 'auth/userAvatar',
      avatarMedia: 'auth/userAvatarMedia',
      shouldDisplayBonusesPin: 'store/shouldDisplayBonusesPin',
      balanceAdjustmentEnabled: 'store/balanceAdjustmentEnabled',
      balanceIsAdjusting: 'auth/balanceIsAdjusting',
      loyaltyEnabled: 'store/loyaltyEnabled',
    }),

    /**
     * @return {-1 | number} - `-1` if no level is achieved.
     */
    userLevelInd() {
      if (!this.loyaltyProfile || !this.bonusesLevels) return null;

      const currentLevel = this.loyaltyProfile.bonuses_level;
      if (!currentLevel) return -1;
      return this.bonusesLevels.findIndex(l => l.id === currentLevel.id);
    },

    levelCardProgresses() {
      return this.bonusesLevels.map((l, ind) => {
        // Whether it's the progress of the next level that needs to be displayed on this card.
        const forNext = this.userLevelInd === ind && this.userLevelInd < this.bonusesLevels.length - 1;
        const forLevelInd = forNext ? ind + 1 : ind;
        const forLevel = this.bonusesLevels[forLevelInd];
        // It's not always true, because otherwise we would be displaying progressbars with value `0` for the latter
        // levels.
        const scaleRelativeToPrevious =
          forLevelInd !== 0
          && this.loyaltyProfile.expenses.value > this.bonusesLevels[forLevelInd - 1].expenses.value;
        const getAmountLeftText = level => unreliablyFormatRublesPrice(
          level.expenses.value - this.loyaltyProfile.expenses.value
        );
        let text;
        if (forNext) {
          text = `До статуса ${forLevel.name} осталось ${getAmountLeftText(forLevel)}`;
        } else {
          if (this.userLevelInd >= ind) {
            text = 'Статус подтвержден';
          } else {
            text = `До статуса осталось ${getAmountLeftText(forLevel)}`;
          }
        }
        return {
          forNext,
          scaleRelativeToPrevious,
          min: scaleRelativeToPrevious ?
            this.bonusesLevels[forLevelInd - 1].expenses
            : createZeroPrice(forLevel.expenses.currency),
          max: forLevel.expenses,
          value: this.loyaltyProfile.expenses,
          text,
        };
      });
    },

    genderOptions() {
      return [
        { label: 'Мужской', id: 'male' },
        { label: 'Женский', id: 'female' },
        { label: 'Не указан', id: 'other' },
      ];
    },

    swiperProps() {
      const component = this;
      return {
        spaceBetween: 8, // 'margin.1'
        slidesPerView: 'auto',
        centeredSlides: true,
        on: {
          init() {
            const unwatch = component.$watch(
              'userLevelInd',
              n => {
                if (typeof n !== 'number') return;
                let to = n;
                if (to === -1) to = 0;
                this.slideTo(to, 0);

                setTimeout(unwatch);
              },
              { immediate: true },
            );
          },
        },
      };
    },

    hasReferralUrls() {
      return !!this.user?.customer?.loyalty?.referral_program?.join_url
    },

    canAdjustBalance() {
      if (this.balanceIsAdjusting) {
        return false
      } else if (!Number.isNaN(nextBalanceAdjustment) && nextBalanceAdjustment > currentTime) {
        return false
      }

      return true
    },
  },

  methods: {
    ...mapActions({
      addNotification: 'notifications/addNotification',
      dispatchBalanceAdjustment: 'auth/dispatchBalanceAdjustment',
    }),

    async adjustBalance() {
      await this.dispatchBalanceAdjustment();
    },

    async saveChanges() {
      this.savingChanges = true;

      try {
        await axios.put('/profile', {
          first_name: this.form.firstName,
          last_name: this.form.lastName,
          gender: this.form.gender,
          birth_date: this.form.birthDate,
        });

        await this.$store.dispatch('auth/initUser'); // This action does  more than just load user data, be careful.

        this.editMode = false;
      } catch (e) {
        console.log(e.response.data)
        const errorMessage = getErrorMessage(e) || 'Произошла неизвестная ошибка'

        this.addNotification({
          id: Math.random().toString().replace('.', ''),
          type: 'error',
          title: 'Не удалось сохранить данные профиля',
          description: errorMessage,
          timeout: 5000,
        })
      } finally {
        this.savingChanges = false;
      }
    },
    getLevelCardProgressPercentage(ind) {
      const progress = this.levelCardProgresses[ind];
      return 100 * progress.value.value / (progress.max.value - progress.min.value);
    },
    formatDatetime(obj) {
      return (new Date(obj.iso_utc)).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
    },
    priceToText,
    getIsDividerAtAlternativePosition(levelInd) {
      return levelInd === this.userLevelInd;
    },
  },
  watch: {
    user(n) {
      this.form = createFormData(n);
    },
  },
  data() {
    return {
      editMode: false,
      form: createFormData(this.user),
      savingChanges: false,
      showPinCode: false,
      pinCode: null,
    };
  },
}
</script>

<style lang="less">
@import '../../pages/profile/common.less';

@r: user-component;
.user-component {
  @apply min-w-0; // Not sure why, but otherwise bonuses levels section would make it span to its `max-width`.
  &__header:extend(.profile__card-header) {}
  &__header-title:extend(.profile__card-header-title) {}
  &__header-button:extend(.profile__card-header-button) {}
  &__base-info-section {
    @apply text-center p-2;
    > * {
      @apply block;
    }
  }
  &__avatar {
    @apply rounded-full w-20 h-20 m-2 mx-auto my-3;
  }
  &__name {
    @apply text-base font-bold leading-tight my-1;
  }
  &__base-info-numbers {
    @apply font-semibold;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    > h5 {
      @apply text-dark-lighter text-xs my-2;
      grid-row-start: 1;
    }
    > p {
      @apply text-primary;
    }
  }
  &__bonuses-levels {
    @apply flex my-3;
    cursor: grab;
    & .swiper-not-ready {
      @apply overflow-x-auto cursor-auto;
      .@{r}__bonuses-level-card {
        @apply mx-1;
      }
    }
  }
  @card-padding: theme('padding.3');
  &__bonuses-level-card {
    @apply p-3 my-2 rounded-lg text-white flex flex-col;
    padding: @card-padding;
    background:
      @payment-card[background-pattern],
      theme('backgroundColor.primary-light');
    border-radius: @payment-card[border-radius];
    flex-basis: @payment-card[width];
    max-width: calc(100% - theme('padding.2'));
    min-height: @payment-card[min-height];
    &.swiper-slide {
      box-sizing: border-box;
      width: unset;
      height: unset;
    }
  }
  &__bonuses-level-card-header {
    @apply flex items-center text-sm font-medium;
  }
  &__bonuses-level-marker {
    @apply rounded-full bg-dark text-2xs font-bold text-center leading-tight;
    padding: calc(theme('padding.1') / 2);
    min-width: theme('width.4');
  }
  &__bonuses-level-name {
    @apply mx-2;
  }
  &__first-name-input,
  &__last-name-input {
    @apply m-2;
  }
  &__bonuses-level-divider {
    @apply border-white mt-2 mb-3 opacity-50;
    margin-left: calc(-1 * @card-padding);
    margin-right: calc(-1 * @card-padding);
  }
  &__bonuses-level-progress-text {
    @apply text-2xs font-light my-1;
  }
  &__bonuses-level-progress-labels {
    @apply flex justify-between text-xs font-medium my-1;
  }
  @progress-bar-height: 6px;
  &__bonuses-level-progress-bar {
    @apply rounded-full;
    height: @progress-bar-height;
    background: rgba(0, 0, 0, 0.3);
  }
  &__bonuses-level-progress-bar-fill {
    @apply bg-white rounded-full h-full max-w-full;
    min-width: @progress-bar-height;
  }
  &__show-pin-button.btn {
    @apply border-white rounded-full text-xs mt-3;
    color: inherit;
  }
  &__detail-info-section {
    @apply px-5;
  }
  &__detail-info-section-item {
    @apply my-4;
    .form-control-label {
      @apply text-dark-lighter text-xs pb-2 mt-2;
    }
  }
}
</style>
